import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LeadTableNew } from "../leads/components/LeadTableNew";
import { LeadboxLayout } from "../components/LeadboxLayout";
import { Button } from "@/components/ui/button";
import { useLead } from "@/hooks/useLead";
import { Card } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { getLanguage } from "@/utils/languageUtils";
import {
  commonTitleLabels,
  commonButtonLabels,
  commonMiscLabels,
  leadUILabels,
} from "@/i18n";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

const AdminLeadbox = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use the lead hook to get leads data
  const { useGetAllLeads } = useLead();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:leads",
    10,
    [10, 25, 50],
  );
  const [currentStatuses, setCurrentStatuses] = useState<string[]>([]);

  // Restore list state when returning from a lead detail (preserve page, statuses, search)
  useEffect(() => {
    const s = location.state as {
      leadListPage?: number;
      leadListStatus?: string;
      leadListStatuses?: string[];
      leadListSearch?: string;
    } | undefined;
    if (s?.leadListPage != null) setCurrentPage(s.leadListPage);
    if (s?.leadListStatuses !== undefined) setCurrentStatuses(Array.isArray(s.leadListStatuses) ? s.leadListStatuses : []);
    else if (s?.leadListStatus !== undefined) setCurrentStatuses(s.leadListStatus ? [s.leadListStatus] : []);
    if (s?.leadListSearch !== undefined) setSearchQuery(s.leadListSearch ?? "");
  }, [location.state]);

  // Get leads data with current parameters (only cars)
  const leadsQuery = useGetAllLeads(
    currentPage,
    itemsPerPage,
    currentStatuses.length ? currentStatuses : undefined,
    "car",
    searchQuery || undefined
  );
  const leads = leadsQuery.data?.data || [];
  const totalLeads = leadsQuery.data?.total || 0;
  const isLoadingLeads = leadsQuery.isLoading;
  const leadsError = leadsQuery.error;

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleLeadClick = (id: string) => {
    navigate(`/admin/leads/${id}`, {
      state: {
        leadListPage: currentPage,
        leadListStatuses: currentStatuses,
        leadListSearch: searchQuery,
      },
    });
  };

  const handleStatusesChange = (statuses: string[]) => {
    setCurrentStatuses(statuses);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // The query will automatically update when state changes
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    // The query will automatically update when state changes
  };

  return (
    <LeadboxLayout>
      <div className="mx-auto space-y-4">
        <div className="flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              {commonTitleLabels(getLanguage()).carLeadbox}
            </h1>
            <Button
              onClick={() => leadsQuery.refetch()}
              variant="outline"
              className="backdrop-blur"
              disabled={isLoadingLeads}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${
                  isLoadingLeads ? "animate-spin" : ""
                }`}
              />
              {commonButtonLabels(getLanguage()).update}
            </Button>
          </div>
        </div>

        <main className="space-y-2">
          <div>
            {isLoadingLeads ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-blue-500 border-solid"></div>
                <span className="ml-2 text-muted-foreground">
                  {commonMiscLabels(getLanguage()).loadingData}
                </span>
              </div>
            ) : leadsError ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <p className="text-red-600 mb-2">
                    {leadUILabels(getLanguage()).couldNotLoadLeads}
                  </p>
                  <Button
                    onClick={() => leadsQuery.refetch()}
                    variant="outline"
                  >
                    {commonMiscLabels(getLanguage()).tryAgain}
                  </Button>
                </div>
              </div>
            ) : (
              <Card className="glass-card p-6">
                <LeadTableNew
                  leads={leads}
                  onLeadClick={handleLeadClick}
                  vehicleType="car"
                  onSearch={handleSearch}
                  isSearching={isLoadingLeads}
                  totalLeads={totalLeads}
                  onStatusesChange={handleStatusesChange}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  selectedStatuses={currentStatuses}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                />
              </Card>
            )}
          </div>
        </main>
      </div>
    </LeadboxLayout>
  );
};

export default AdminLeadbox;
